import React, {useEffect, useState} from 'react';
import {StyleSheet, View} from 'react-native';
import {useMutation, useQuery, useQueryClient} from '@tanstack/react-query';
import {useTranslation} from 'react-i18next';

import {
  fetchNotificationSettings,
  saveNotificationSettings,
} from '@/entities/notification-settings/api';
import type {NotificationSettings} from '@/entities/notification-settings/types';
import {Banner} from '@/shared/ui/banner';
import {AppButton} from '@/shared/ui/button';
import {Card} from '@/shared/ui/card';
import {AppInput} from '@/shared/ui/input';
import {Skeleton} from '@/shared/ui/skeleton';
import {Toggle} from '@/shared/ui/toggle';
import {radii, spacing} from '@/shared/ui/theme';

/**
 * The company-wide notification kill-switch + absence digest, edited as one
 * draft and saved in one PATCH. `draft` is seeded from the query on load (and
 * re-seeded whenever the server value changes under us) rather than read
 * straight from `q.data`, so a value the user is mid-edit never gets clobbered
 * by a background refetch — same pattern as TerminalSettingsForm's draft.
 *
 * Client-side HH:MM validation only blocks the Save button; sv_control
 * validates `digest_time` again server-side (Task 3), so a client bypass
 * cannot corrupt the schedule.
 */
const HHMM = /^([01]\d|2[0-3]):[0-5]\d$/;

const styles = StyleSheet.create({
  root: {gap: spacing(4), paddingBottom: spacing(6)},
  card: {gap: spacing(3)},
});

export function NotificationSettingsForm({companyId}: {companyId: string}) {
  const {t} = useTranslation();
  const qc = useQueryClient();

  const q = useQuery({
    queryKey: ['notification-settings', companyId],
    queryFn: () => fetchNotificationSettings(companyId),
  });

  const [draft, setDraft] = useState<NotificationSettings | null>(null);
  useEffect(() => {
    if (q.data) {
      // Defaults filled in for what the server omits, mirroring the gateway's
      // own serde defaults: a backend older than 2026-08-18 answers without the
      // report fields, and an undefined time would fail the HH:MM check and
      // disable Save on a screen that has nothing wrong with it.
      setDraft({
        ...q.data,
        daily_report_enabled: q.data.daily_report_enabled ?? false,
        daily_report_time: q.data.daily_report_time || '09:00',
      });
    }
  }, [q.data]);

  const save = useMutation({
    mutationFn: (s: NotificationSettings) => saveNotificationSettings(companyId, s),
    onSuccess: fresh => {
      qc.setQueryData(['notification-settings', companyId], fresh);
      setDraft(fresh);
    },
  });

  if (q.isLoading || !draft) {
    return <Skeleton height={220} radius={radii.lg} />;
  }

  const set = (patch: Partial<NotificationSettings>) =>
    setDraft(d => (d ? {...d, ...patch} : d));
  // Both hours are checked whether or not their mailing is on, because that is
  // what sv_control does: its setter normalises both unconditionally and answers
  // 400 on either. Validating only the enabled one would let "9" typed into the
  // digest field and then switched off reach the server as an error.
  const digestTimeValid = HHMM.test(draft.digest_time);
  const reportTimeValid = HHMM.test(draft.daily_report_time);
  const timeValid = digestTimeValid && reportTimeValid;

  // A time field shows while its mailing is on — and also whenever its value is
  // invalid, even with the mailing off. Otherwise a bad value hidden behind a
  // switched-off toggle disables Save with nothing on screen to fix.
  const showDigestTime = draft.digest_enabled || !digestTimeValid;
  const showReportTime = draft.daily_report_enabled || !reportTimeValid;

  return (
    <View style={styles.root}>
      <Card padding={4} style={styles.card}>
        <Toggle
          label={t('notifSettings.arrivals')}
          hint={t('notifSettings.arrivalsHint')}
          value={draft.arrivals_enabled}
          onChange={v => set({arrivals_enabled: v})}
        />
        <Toggle
          label={t('notifSettings.departures')}
          hint={t('notifSettings.departuresHint')}
          value={draft.departures_enabled}
          onChange={v => set({departures_enabled: v})}
        />
        <Toggle
          label={t('notifSettings.digest')}
          hint={t('notifSettings.digestHint')}
          value={draft.digest_enabled}
          onChange={v => set({digest_enabled: v})}
        />

        {/* Hidden while the digest is off — a disabled field for a switched-off
            feature is one more thing to read and nothing to decide. */}
        {showDigestTime ? (
          <AppInput
            label={t('notifSettings.digestTime')}
            value={draft.digest_time}
            onChangeText={v => set({digest_time: v})}
            placeholder="09:30"
            keyboardType="numbers-and-punctuation"
            maxLength={5}
            error={digestTimeValid ? undefined : t('notifSettings.digestTimeError')}
            testID="digest-time-input"
          />
        ) : null}

        {/* Its own switch, not the digest's. The two are different mailings with
            different hours, and sv_control stores them under different keys — a
            shared toggle would send both. */}
        <Toggle
          label={t('notifSettings.dailyReport')}
          hint={t('notifSettings.dailyReportHint')}
          value={draft.daily_report_enabled}
          onChange={v => set({daily_report_enabled: v})}
        />
        {showReportTime ? (
          <AppInput
            label={t('notifSettings.dailyReportTime')}
            value={draft.daily_report_time}
            onChangeText={v => set({daily_report_time: v})}
            placeholder="09:00"
            keyboardType="numbers-and-punctuation"
            maxLength={5}
            error={reportTimeValid ? undefined : t('notifSettings.digestTimeError')}
            testID="daily-report-time-input"
          />
        ) : null}
      </Card>

      <AppButton
        title={t('actions.save')}
        size="lg"
        onPress={() => {
          if (timeValid) {
            save.mutate(draft);
          }
        }}
        disabled={!timeValid || save.isPending}
        loading={save.isPending}
      />
      {save.isError ? <Banner tone="error" title={t('notifSettings.saveError')} /> : null}
      {save.isSuccess ? <Banner tone="success" title={t('notifSettings.saved')} /> : null}
    </View>
  );
}
